import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../../common/types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { platformMeta, platformSolidFill } from '../../platformMeta';
import { SocialPlatform } from '../../types';
import { legacyPlanMessages as planMessages } from './legacyPlanMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PlanChannelChipsProps {
  /** The connected and manual channels of the artist, in platform order. */
  platforms: SocialPlatform[];
  selected: SocialPlatform[];
  onToggle: (platform: SocialPlatform) => void;
}

interface ChipProps {
  platform: SocialPlatform;
  isOn: boolean;
  onToggle: (platform: SocialPlatform) => void;
}

const ChipComponent: React.FC<ChipProps> = ({ platform, isOn, onToggle }) => {
  const meta = platformMeta[platform];
  const toggle = useCallback(() => onToggle(platform), [onToggle, platform]);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isOn}
      className={clsx(styles.chan, isOn && styles.chanOn)}
      onClick={toggle}
    >
      <span className={styles.chanMark} style={{ background: platformSolidFill(platform) }} aria-hidden>
        <FontAwesomeIcon icon={meta.icon} />
      </span>
      {meta.name}
    </button>
  );
};

const Chip = TypedMemo(ChipComponent);

/**
 * "Post on" (designs m3, m4): one chip per connected or manual channel. With no channel, only
 * "Connect a channel" shows; it opens the Channels screen.
 */
const PlanChannelChipsComponent: React.FC<PlanChannelChipsProps> = ({ platforms, selected, onToggle }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);

  return (
    <div className="d-flex flex-column gap-2">
      <span className={styles.label}>{intl.formatMessage(planMessages.postOn)}</span>
      <div className={styles.chanrow}>
        {platforms.map((platform) => (
          <Chip key={platform} platform={platform} isOn={selected.includes(platform)} onToggle={onToggle} />
        ))}
        <button type="button" className={styles.linkbtn} onClick={openChannels}>
          {intl.formatMessage(planMessages.connectChannel)}
        </button>
      </div>
      {platforms.length === 0 && <span className={styles.tiny}>{intl.formatMessage(planMessages.noChannel)}</span>}
    </div>
  );
};

export const PlanChannelChips = TypedMemo(PlanChannelChipsComponent);
